import { useState, useEffect } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  ArrowLeft,
  Play,
  Pause,
  Power,
  Clock,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Loader2,
  DollarSign,
  Users,
  Heart,
  TrendingUp,
  Target,
  Zap,
  Bot,
  Activity,
  Brain,
  ChevronRight,
  Settings,
  History,
  BarChart3,
} from "lucide-react"
import { toast } from "sonner"
import { useOrganization } from "@/contexts/organization-context"
import { agentsService, type Agent, type AgentExecution, type SavedReport } from "@/services/agents"
import { llmConfigService, type LLMConfig } from "@/services/llm-config"

// Helper function to convert cron expression to friendly text
const formatCronToFriendly = (cron: string | null | undefined): string => {
  if (!cron) return 'Manual only'

  // Parse common cron patterns
  const parts = cron.split(' ')
  if (parts.length !== 5) return cron

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts

  // Daily at specific time: "0 9 * * *" -> "Daily at 9:00 AM UTC"
  if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    const hourNum = parseInt(hour)
    const minNum = parseInt(minute)
    const period = hourNum >= 12 ? 'PM' : 'AM'
    const displayHour = hourNum > 12 ? hourNum - 12 : (hourNum === 0 ? 12 : hourNum)
    const displayMin = minNum.toString().padStart(2, '0')
    return `Daily at ${displayHour}:${displayMin} ${period} UTC`
  }

  // Weekly patterns: "0 9 * * 1" -> "Every Monday at 9:00 AM UTC"
  if (dayOfMonth === '*' && month === '*' && dayOfWeek !== '*') {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    const dayName = days[parseInt(dayOfWeek)] || dayOfWeek
    const hourNum = parseInt(hour)
    const minNum = parseInt(minute)
    const period = hourNum >= 12 ? 'PM' : 'AM'
    const displayHour = hourNum > 12 ? hourNum - 12 : (hourNum === 0 ? 12 : hourNum)
    const displayMin = minNum.toString().padStart(2, '0')
    return `Every ${dayName} at ${displayHour}:${displayMin} ${period} UTC`
  }

  // Fallback: show the cron expression
  return cron
}

// Icon mapping
const iconMap: Record<string, typeof Users> = {
  "dollar-sign": DollarSign,
  "users": Users,
  "heart": Heart,
  "trending-up": TrendingUp,
  "target": Target,
  "zap": Zap,
  "activity": Activity,
  "retention": Heart,
  "monetization": DollarSign,
}

// Status configuration
const statusConfig: Record<string, { icon: typeof CheckCircle2; color: string; bgColor: string; label: string }> = {
  draft: { icon: Clock, color: "text-slate-500", bgColor: "bg-slate-100", label: "Draft" },
  active: { icon: CheckCircle2, color: "text-emerald-600", bgColor: "bg-emerald-50", label: "Active" },
  paused: { icon: Pause, color: "text-amber-600", bgColor: "bg-amber-50", label: "Paused" },
  disabled: { icon: AlertCircle, color: "text-red-600", bgColor: "bg-red-50", label: "Disabled" },
}

// Execution status configuration
const executionStatusConfig: Record<string, { icon: typeof CheckCircle2; color: string; bgColor: string }> = {
  running: { icon: Loader2, color: "text-blue-600", bgColor: "bg-blue-50" },
  completed: { icon: CheckCircle2, color: "text-emerald-600", bgColor: "bg-emerald-50" },
  failed: { icon: XCircle, color: "text-red-600", bgColor: "bg-red-50" },
  cancelled: { icon: AlertCircle, color: "text-amber-600", bgColor: "bg-amber-50" },
}

export function AgentDetailPage() {
  const { agentId, orgSlug } = useParams<{ agentId: string; orgSlug: string }>()
  const { currentOrganization } = useOrganization()
  const navigate = useNavigate()

  const [agent, setAgent] = useState<Agent | null>(null)
  const [executions, setExecutions] = useState<AgentExecution[]>([])
  const [llmConfig, setLlmConfig] = useState<LLMConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [runningAgent, setRunningAgent] = useState(false)
  const [savedReports, setSavedReports] = useState<SavedReport[]>([])

  useEffect(() => {
    if (agentId && currentOrganization?.id) {
      loadAgentData()
    }
  }, [agentId, currentOrganization?.id])

  const loadAgentData = async () => {
    if (!agentId || !currentOrganization?.id) return

    setLoading(true)
    try {
      const [agentData, executionsData, reportsData] = await Promise.all([
        agentsService.getAgent(parseInt(agentId)),
        agentsService.getExecutions(parseInt(agentId)),
        agentsService.getSavedReports().catch(() => [] as SavedReport[]),
      ])
      setAgent(agentData)
      setExecutions(executionsData)
      setSavedReports(reportsData)

      // Load LLM config for organization budget
      try {
        const config = await llmConfigService.getConfig(currentOrganization.id)
        setLlmConfig(config)
      } catch (e) {
        // LLM config may not exist, use defaults
        console.debug('LLM config not found, using defaults')
      }
    } catch (error: any) {
      console.error('Error loading agent:', error)
      toast.error(`Failed to load agent: ${error.response?.data?.detail || error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleRunAgent = async () => {
    if (!agent || !currentOrganization?.id) return

    setRunningAgent(true)
    try {
      const result = await agentsService.runAgent(agent.id, currentOrganization.id)
      toast.success(`Agent execution started!`, {
        description: `Workflow ID: ${result.workflow_id}`
      })
      // Reload executions after a short delay
      setTimeout(() => loadAgentData(), 2000)
    } catch (error: any) {
      toast.error(`Failed to run agent: ${error.response?.data?.detail || error.message}`)
    } finally {
      setRunningAgent(false)
    }
  }

  const handleActivate = async () => {
    if (!agent) return
    try {
      const updated = await agentsService.activateAgent(agent.id)
      setAgent(updated)
      toast.success("Agent activated!")
    } catch (error: any) {
      toast.error(`Failed to activate: ${error.response?.data?.detail || error.message}`)
    }
  }

  const handlePause = async () => {
    if (!agent) return
    try {
      const updated = await agentsService.pauseAgent(agent.id)
      setAgent(updated)
      toast.success("Agent paused!")
    } catch (error: any) {
      toast.error(`Failed to pause: ${error.response?.data?.detail || error.message}`)
    }
  }

  const formatDuration = (ms: number | null) => {
    if (!ms) return "-"
    if (ms < 1000) return `${ms}ms`
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
    return `${(ms / 60000).toFixed(1)}m`
  }

  const formatCost = (cost: number | null) => {
    if (!cost) return "-"
    return `$${cost.toFixed(4)}`
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
        <p className="text-slate-500">Loading agent...</p>
      </div>
    )
  }

  if (!agent) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <AlertCircle className="h-10 w-10 text-red-500" />
        <p className="text-slate-500">Agent not found</p>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>
      </div>
    )
  }

  const IconComponent = iconMap[agent.icon || "users"] || Bot
  const status = statusConfig[agent.status || "draft"]
  const StatusIcon = status.icon
  const agentColor = agent.color?.startsWith('#') ? agent.color : '#3b82f6'

  // Dataset (bound saved report) + Control (target metrics)
  const datasetReport = agent.dataset_report_id != null
    ? savedReports.find(r => r.id === agent.dataset_report_id)
    : null
  const controlMetrics = (agent.control || []).map(c => c.name)

  // Calculate stats
  const completedExecutions = executions.filter(e => e.status === 'completed')
  const failedExecutions = executions.filter(e => e.status === 'failed')
  const totalCost = executions.reduce((sum, e) => sum + (e.llm_cost_usd || 0), 0)
  const avgDuration = completedExecutions.length > 0
    ? completedExecutions.reduce((sum, e) => sum + (e.duration_ms || 0), 0) / completedExecutions.length
    : 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div
          className="h-14 w-14 rounded-xl flex items-center justify-center shadow-md"
          style={{ backgroundColor: agentColor }}
        >
          <IconComponent className="h-7 w-7 text-white" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900">{agent.name}</h1>
            <Badge
              variant="secondary"
              className={`${status.bgColor} ${status.color} border-0 gap-1`}
            >
              <StatusIcon className="h-3 w-3" />
              {status.label}
            </Badge>
          </div>
          <p className="text-slate-500 mt-1">{agent.description || "No description"}</p>
        </div>
        <div className="flex gap-2">
          {agent.status === 'active' ? (
            <Button variant="outline" onClick={handlePause}>
              <Pause className="h-4 w-4 mr-2" />
              Pause
            </Button>
          ) : (
            <Button variant="outline" onClick={handleActivate}>
              <Power className="h-4 w-4 mr-2" />
              Activate
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <History className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{executions.length}</p>
                <p className="text-sm text-slate-500">Total Runs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{completedExecutions.length}</p>
                <p className="text-sm text-slate-500">Successful</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{formatDuration(avgDuration)}</p>
                <p className="text-sm text-slate-500">Avg Duration</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{formatCost(totalCost)}</p>
                <p className="text-sm text-slate-500">Total LLM Cost</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="executions" className="space-y-4">
        <TabsList>
          <TabsTrigger value="executions" className="gap-2">
            <History className="h-4 w-4" />
            Executions
          </TabsTrigger>
          <TabsTrigger value="config" className="gap-2">
            <Settings className="h-4 w-4" />
            Configuration
          </TabsTrigger>
          <TabsTrigger value="stats" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Statistics
          </TabsTrigger>
        </TabsList>

        {/* Executions Tab */}
        <TabsContent value="executions">
          <Card>
            <CardHeader>
              <CardTitle>Recent Executions</CardTitle>
              <CardDescription>History of agent execution cycles</CardDescription>
            </CardHeader>
            <CardContent>
              {executions.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No executions yet</p>
                  <p className="text-sm mt-1">Run the agent to see execution history</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Trigger</TableHead>
                      <TableHead>Started</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead>Tokens</TableHead>
                      <TableHead>Cost</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {executions.map((execution) => {
                      const execStatus = executionStatusConfig[execution.status] || executionStatusConfig.completed
                      const ExecStatusIcon = execStatus.icon

                      return (
                        <TableRow key={execution.id}>
                          <TableCell className="font-mono text-sm">#{execution.id}</TableCell>
                          <TableCell>
                            <Badge
                              variant="secondary"
                              className={`${execStatus.bgColor} ${execStatus.color} border-0 gap-1`}
                            >
                              <ExecStatusIcon className={`h-3 w-3 ${execution.status === 'running' ? 'animate-spin' : ''}`} />
                              {execution.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="capitalize">{execution.trigger_type}</TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {execution.started_at
                              ? new Date(execution.started_at).toLocaleString()
                              : "-"}
                          </TableCell>
                          <TableCell>{formatDuration(execution.duration_ms)}</TableCell>
                          <TableCell className="text-sm">
                            {execution.llm_model_used ? (
                              <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">
                                {execution.llm_model_used.split('-').slice(0, 2).join('-')}
                              </span>
                            ) : "-"}
                          </TableCell>
                          <TableCell className="text-sm">
                            {execution.tokens_input || execution.tokens_output ? (
                              <span className="text-slate-600">
                                {(execution.tokens_input || 0).toLocaleString()} / {(execution.tokens_output || 0).toLocaleString()}
                              </span>
                            ) : "-"}
                          </TableCell>
                          <TableCell>{formatCost(execution.llm_cost_usd)}</TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/${currentOrganization?.slug}/agents/${agentId}/executions/${execution.id}`)}
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Configuration Tab */}
        <TabsContent value="config">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Agent Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Agent Type</p>
                    <p className="font-medium capitalize">{agent.agent_type?.replace('_', ' ') || 'Custom'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Status</p>
                    <p className="font-medium capitalize">{agent.status || 'Draft'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Schedule</p>
                    <p className="text-sm">{formatCronToFriendly(agent.schedule_cron)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Last Seen</p>
                    <p className="text-sm">
                      {agent.last_seen_at
                        ? new Date(agent.last_seen_at).toLocaleString()
                        : 'Never'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Dataset</p>
                    {datasetReport ? (
                      <Link
                        to={`/${orgSlug}/reports?report=${encodeURIComponent(
                          datasetReport.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_-]/g, '').replace(/_+/g, '_')
                        )}`}
                        className="font-medium text-blue-600 hover:underline"
                      >
                        {datasetReport.name}
                      </Link>
                    ) : (
                      <p className="font-medium text-slate-400">No dataset</p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Control</p>
                    <p className="font-medium">
                      {controlMetrics.length > 0 ? controlMetrics.join(', ') : '—'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="h-5 w-5" />
                  LLM Usage
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {executions.length > 0 && executions[0].llm_model_used ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Last Model Used</p>
                      <p className="font-mono text-sm">{executions[0].llm_model_used}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Last Tokens</p>
                      <p className="font-medium">
                        {(executions[0].tokens_input || 0).toLocaleString()} in / {(executions[0].tokens_output || 0).toLocaleString()} out
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Last Cost</p>
                      <p className="font-medium">{formatCost(executions[0].llm_cost_usd)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Last Cycle</p>
                      <p className="font-medium">#{agent.last_cycle_number || '-'}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No execution data available yet</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Budget Limits
                </CardTitle>
                <CardDescription>
                  Organization-wide AI budget ({' '}
                  <Link to="/settings?tab=ai" className="text-blue-600 hover:underline">
                    Configure in Settings
                  </Link>
                  )
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Daily Budget</p>
                    <p className="font-medium">${llmConfig?.daily_budget_usd?.toFixed(2) || '20.00'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Monthly Budget</p>
                    <p className="font-medium">${llmConfig?.monthly_budget_usd?.toFixed(2) || '500.00'}</p>
                  </div>
                </div>
                <div className="pt-2">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-500">Current daily spend</span>
                    <span className="font-medium">${llmConfig?.current_day_spend_usd?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${Math.min(((llmConfig?.current_day_spend_usd || 0) / (llmConfig?.daily_budget_usd || 20)) * 100, 100)}%` }}
                    />
                  </div>
                </div>
                <div className="pt-2">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-500">Current monthly spend</span>
                    <span className="font-medium">${llmConfig?.current_month_spend_usd?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${Math.min(((llmConfig?.current_month_spend_usd || 0) / (llmConfig?.monthly_budget_usd || 500)) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {agent.objective_function && (
              <Card>
                <CardHeader>
                  <CardTitle>Objective Function</CardTitle>
                </CardHeader>
                <CardContent>
                  <pre className="text-sm bg-slate-50 p-4 rounded-lg overflow-x-auto whitespace-pre-wrap">
                    {agent.objective_function}
                  </pre>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Statistics Tab */}
        <TabsContent value="stats">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Execution Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Success Rate</span>
                    <span className="font-bold text-emerald-600">
                      {executions.length > 0
                        ? `${((completedExecutions.length / executions.length) * 100).toFixed(1)}%`
                        : '-'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Failed Executions</span>
                    <span className="font-bold text-red-600">{failedExecutions.length}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Average Duration</span>
                    <span className="font-bold">{formatDuration(avgDuration)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Total LLM Cost</span>
                    <span className="font-bold">{formatCost(totalCost)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Token Usage</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Total Input Tokens</span>
                    <span className="font-bold">
                      {executions.reduce((sum, e) => sum + (e.tokens_input || 0), 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Total Output Tokens</span>
                    <span className="font-bold">
                      {executions.reduce((sum, e) => sum + (e.tokens_output || 0), 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Avg Tokens/Run</span>
                    <span className="font-bold">
                      {executions.length > 0
                        ? Math.round(executions.reduce((sum, e) => sum + (e.tokens_input || 0) + (e.tokens_output || 0), 0) / executions.length).toLocaleString()
                        : '-'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}
